import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { IoIosArrowRoundBack } from "react-icons/io";
import { BsCloudUpload } from "react-icons/bs";

import EditClassiedPostCommonFields from "./EditClassiedPostCommonFields";
import styles from "./Form.module.css";

import { useGetPreSignedUrlMutation } from "../../redux/api/uploadApi";

const EditClassifiedPost = ({
  classified,
  classified_id,
  setEdit,
  refetch,
}) => {
  const navigate = useNavigate();
  const filePikerRef = useRef(null);

  const [getPreSignedUrl] = useGetPreSignedUrlMutation();

  const [loading, setLoading] = useState(false);
  const [imgsrc, setImagesrc] = useState("");
  const [imgeFile, setImgeFile] = useState(null);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    country_id: "KW",
    district_id: "",
    area_id: "",
    address: "",
    title: "",
    mobile: "",
    price: "",
    description: "",

    // Other possible common classified fields
    isItOldNotNew: "",
    careerLevel: "",
    leaseType: "",
    buildingType: "",
    street: "",
    blockNo: "",
    buildingNo: "",
    floor: "",
    flatNo: "",
    rooms: "",
    livingRooms: "",
    bathrooms: "",
    carParking: false,
    bikeParking: false,
  });

  /*
   * --------------------------------------------------
   * Classified Type
   * --------------------------------------------------
   */

  const classifiedType = classified?.classifiedType
    ? classified.classifiedType
    : "";

  /*
   * --------------------------------------------------
   * Fill form from classified details
   * --------------------------------------------------
   */

  useEffect(() => {
    if (!classified) return;

    setFormData({
      country_id: classified.country_id?._id || classified.country_id || "KW",

      district_id: classified.district_id?._id || classified.district_id || "",

      area_id: classified.area_id?._id || classified.area_id || "",

      address: classified.address || "",

      title: classified.title || "",

      mobile: classified.mobile || "",

      price:
        classified.price !== undefined && classified.price !== null
          ? classified.price
          : "",

      description: classified.description || "",

      isItOldNotNew:
        classified.isItOldNotNew === true
          ? true
          : classified.isItOldNotNew === false
            ? false
            : classified.isItOldNotNew || "",

      careerLevel: classified.careerLevel?._id || classified.careerLevel || "",

      leaseType: classified.leaseType?._id || classified.leaseType || "",

      buildingType:
        classified.buildingType?._id || classified.buildingType || "",

      street: classified.street || "",

      blockNo: classified.blockNo !== undefined ? classified.blockNo : "",

      buildingNo:
        classified.buildingNo !== undefined ? classified.buildingNo : "",

      floor: classified.floor !== undefined ? classified.floor : "",

      flatNo: classified.flatNo !== undefined ? classified.flatNo : "",

      rooms: classified.rooms !== undefined ? classified.rooms : "",

      livingRooms:
        classified.livingRooms !== undefined ? classified.livingRooms : "",

      bathrooms: classified.bathrooms !== undefined ? classified.bathrooms : "",

      carParking: Boolean(classified.carParking),

      bikeParking: Boolean(classified.bikeParking),
    });

    /*
     * Existing image
     */

    if (classified.media && classified.media.length > 0) {
      const image = classified.media.find((item) => item.type === "image");

      if (image?.url) {
        setImagesrc(image.url);
      }
    }
  }, [classified]);

  /*
   * --------------------------------------------------
   * Handle change
   * --------------------------------------------------
   */

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  /*
   * --------------------------------------------------
   * Validation
   * --------------------------------------------------
   */

  const validateForm = () => {
    const newErrors = {};

    if (!formData.country_id) {
      newErrors.country = "Country is required";
    }

    if (!formData.district_id) {
      newErrors.district = "District is required";
    }

    if (!formData.address) {
      newErrors.address = "Other address is required";
    }

    if (!formData.title) {
      newErrors.title = "Title is required";
    }

    if (!formData.mobile) {
      newErrors.mobile = "Mobile is required";
    } else if (!/^\d*$/.test(formData.mobile)) {
      newErrors.mobile = "Mobile must be a positive number";
    } else if (Number(formData.mobile) < 0) {
      newErrors.mobile = "Mobile cannot be negative";
    }

    if (!formData.price) {
      newErrors.price = "Price is required";
    } else if (!/^\d*$/.test(formData.price)) {
      newErrors.price = "Price must be a positive number";
    } else if (Number(formData.price) < 0) {
      newErrors.price = "Price cannot be negative";
    }

    /*
     * Buy & Sell
     */

    if (classifiedType === "buy & sell") {
      if (!formData.isItOldNotNew) {
        newErrors.isItOldNotNew = "Old or New is required";
      }
    }

    /*
     * Jobs
     */

    if (classifiedType === "jobs") {
      if (!formData.careerLevel) {
        newErrors.careerLevel = "Career Type is required";
      }
    }

    /*
     * Property
     */

    if (classifiedType === "property") {
      if (!formData.leaseType) {
        newErrors.leaseType = "Lease Type is required";
      }

      if (!formData.buildingType) {
        newErrors.buildingType = "Building Type is required";
      }

      if (!formData.street) {
        newErrors.street = "Street is required";
      }

      if (!formData.buildingNo) {
        newErrors.buildingNo = "Building number is required";
      }

      if (formData.buildingNo && !/^\d*$/.test(formData.buildingNo)) {
        newErrors.buildingNo = "Building number must be a positive number";
      }

      if (!formData.floor) {
        newErrors.floor = "Floor is required";
      }

      if (formData.floor && !/^\d*$/.test(formData.floor)) {
        newErrors.floor = "Floor must be a positive number";
      }

      if (!formData.flatNo) {
        newErrors.flatNo = "Flat number is required";
      }

      if (formData.flatNo && !/^\d*$/.test(formData.flatNo)) {
        newErrors.flatNo = "Flat number must be a positive number";
      }

      if (!formData.blockNo) {
        newErrors.blockNo = "Block number is required";
      }

      if (formData.blockNo && !/^\d*$/.test(formData.blockNo)) {
        newErrors.blockNo = "Block number must be a positive number";
      }

      if (!formData.rooms) {
        newErrors.rooms = "Number of rooms is required";
      }

      if (formData.rooms && !/^\d*$/.test(formData.rooms)) {
        newErrors.rooms = "Rooms must be a positive number";
      }

      if (!formData.livingRooms) {
        newErrors.livingRooms = "Number of living rooms is required";
      }

      if (formData.livingRooms && !/^\d*$/.test(formData.livingRooms)) {
        newErrors.livingRooms = "Living rooms must be a positive number";
      }

      if (formData.bathrooms && !/^\d*$/.test(formData.bathrooms)) {
        newErrors.bathrooms = "Bathrooms must be a positive number";
      }
    }

    return newErrors;
  };

  /*
   * --------------------------------------------------
   * Submit
   * --------------------------------------------------
   */

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) {
      return;
    }

    const validationErrors = validateForm();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setLoading(true);

    try {
      /*
       * New image selected
       */

      if (imgeFile) {
        await getPresignedUrlAndUpload();
      } else {
        /*
         * Keep existing image
         */

        await updateClassified();
      }
    } catch (error) {
      console.error(error);

      setLoading(false);

      alert(error?.message || "Failed to update classified");
    }
  };

  /*
   * --------------------------------------------------
   * Get Presigned URL
   * --------------------------------------------------
   */

  const getPresignedUrlAndUpload = async () => {
    try {
      const res = await getPreSignedUrl({
        fileNames: [imgeFile.name],
        contentType: imgeFile.type,
        flocation: "posts",
      });

      if (res.error) {
        handleApiError(res.error);
        return;
      }

      const presigned = res.data?.preSignedUrls?.[0];

      if (!presigned) {
        throw new Error("Presigned URL not received");
      }

      await uploadFile(
        presigned.preSignedUrlToUplaodToAWS,
        presigned.pathNametoStoreInDBaftersucessfullupload,
      );
    } catch (error) {
      console.error(error);

      setLoading(false);

      alert(error?.message || "Image upload failed");
    }
  };

  /*
   * --------------------------------------------------
   * Upload image
   * --------------------------------------------------
   */

  const uploadFile = async (presignedURL, storeTODB) => {
    try {
      await axios.put(presignedURL, imgeFile, {
        headers: {
          "Content-Type": imgeFile.type,
        },

        onUploadProgress: (progressEvent) => {
          const { loaded, total } = progressEvent;

          if (total) {
            const percent = Math.floor((loaded * 100) / total);

            console.log(`Uploading ${percent}%`);
          }
        },
      });

      /*
       * Image uploaded.
       * Now update classified.
       */

      await updateClassified(storeTODB);
    } catch (error) {
      console.error(error);

      setLoading(false);

      alert("Image upload failed");
    }
  };

  /*
   * --------------------------------------------------
   * Update Classified API
   * --------------------------------------------------
   */

  const updateClassified = async (newImageUrl = null) => {
    try {
      const payload = {
        ...formData,
      };

      /*
       * Backend expects boolean
       * for old/new.
       */

      if (classifiedType === "buy & sell") {
        payload.isItOldNotNew = formData.isItOldNotNew === "old";
      }

      /*
       * If new image uploaded,
       * replace media.
       *
       * If no new image:
       * don't send media, so the
       * existing media remains unchanged.
       */

      if (newImageUrl) {
        payload.media = [
          {
            url: newImageUrl,
            type: "image",
          },
        ];
      }

      /*
       * Don't send fields that are
       * only frontend state if necessary.
       */
      const user = JSON.parse(localStorage.getItem("RaselListing"));

      const response = await fetch(
        `${process.env.REACT_APP_backendurl}/api/classifieds/edit-classified/${classified_id}`,
        {
          method: "POST",

          headers: {
            "Content-Type": "application/json",

            ...(user?.token && {
              Authorization: `Bearer ${user?.token}`,
            }),
          },

          body: JSON.stringify(payload),
        },
      );

      const result = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          alert("Not authorized, login again");

          window.location.href = "/rm-admin";

          return;
        }

        throw new Error(result?.error || "Failed to update classified");
      }

      if (result.classifiedUpdated) {
        alert("Classified updated successfully");
      }
      refetch();
      setEdit(false);
    } catch (error) {
      console.error(error);

      alert(error?.message || "Failed to update classified");
    } finally {
      setLoading(false);
    }
  };

  /*
   * --------------------------------------------------
   * API Error
   * --------------------------------------------------
   */

  const handleApiError = (apiError) => {
    setLoading(false);

    if (apiError?.status === 401) {
      alert("Not authorized, login again");

      window.location.href = "/rm-admin";

      return;
    }

    alert(apiError?.data?.error || "Something went wrong");
  };

  /*
   * --------------------------------------------------
   * Image picker
   * --------------------------------------------------
   */

  const filePickerHandeler = (event) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];

      setImgeFile(file);

      const reader = new FileReader();

      reader.readAsDataURL(file);

      reader.addEventListener("load", () => {
        setImagesrc(reader.result);
      });
    }
  };

  /*
   * --------------------------------------------------
   * Render
   * --------------------------------------------------
   */

  return (
    <div
      className="root-top-margin"
      style={{
        background: "white",
      }}
    >
      <IoIosArrowRoundBack
        size={40}
        style={{
          cursor: "pointer",
        }}
        onClick={() => navigate(-1)}
      />

      <div
        style={{
          background: "white",
          width: "80%",
          margin: "auto",
          padding: "10px",
        }}
      >
        {/* IMAGE */}

        {/* <div className={styles.imageWrapper}>
          <input
            type="file"
            ref={filePikerRef}
            accept="image/*"
            className="display-n"
            onChange={filePickerHandeler}
          />

          {imgsrc && (
            <div className="text-align-center">
              <img src={imgsrc} alt="classified" className={styles.catImage} />

              <div
                className={styles.uploadImgButton}
                onClick={() => {
                  if (loading) {
                    alert("Please wait, updating...");
                  } else {
                    filePikerRef.current?.click();
                  }
                }}
              >
                Change Image <BsCloudUpload />
              </div>
            </div>
          )}

          {!imgsrc && (
            <div
              className={styles.uploadImgButton}
              onClick={() => {
                if (!loading) {
                  filePikerRef.current?.click();
                }
              }}
            >
              Add Image <BsCloudUpload />
            </div>
          )}
        </div> */}

        {/* FORM */}

        <form className={styles.form} onSubmit={handleSubmit}>
          <EditClassiedPostCommonFields
            formData={formData}
            handleChange={handleChange}
            errors={errors}
            classifiedType={classifiedType}
            classified_id={
              classified?.category_id?._id ||
              classified?.category_id ||
              classified_id
            }
          />

          <div
            className="display-flex"
            style={{
              gap: "4px",
            }}
          >
            <div className={styles.fieldFull}>
              <button
                type="submit"
                className={styles.submitButton}
                disabled={loading}
              >
                {loading ? "Updating..." : "Update"}
              </button>
            </div>

            {loading && (
              <div
                style={{
                  display: "flex",
                  alignItems: "end",
                }}
              >
                <span className="loader-spiner"></span>

                <span className="text-primary">Updating....</span>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditClassifiedPost;
